
import React from "react";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Shield, Clock, Cog, Terminal, Download, AlertTriangle, Lock } from "lucide-react";

interface Activity {
  id: string;
  user: string;
  action: string;
  device: string;
  deviceId: string;
  timeAgo: string;
  type: "admin" | "installation" | "security" | "update" | "alert" | "mdm";
  duration: string;
}

export function RecentActivities() {
  // Mock data for recent activities - now more IT admin focused
  const activities: Activity[] = [
    {
      id: "1",
      user: "Admin",
      action: "deployed security policy",
      device: "Engineering MacBooks",
      deviceId: "policy-eng-mac",
      timeAgo: "12 minutes ago",
      type: "security",
      duration: "Permanent"
    },
    {
      id: "2",
      user: "System",
      action: "detected unauthorized software",
      device: "MacBook Pro - Marketing",
      deviceId: "mbp-mkt-003",
      timeAgo: "34 minutes ago",
      type: "alert",
      duration: ""
    },
    {
      id: "3",
      user: "MDM",
      action: "pushed OS update",
      device: "Windows Devices",
      deviceId: "policy-win-update",
      timeAgo: "1 hour ago",
      type: "update",
      duration: "14.1.2"
    },
    {
      id: "4",
      user: "Admin",
      action: "granted admin rights",
      device: "MacBook Air - Developer",
      deviceId: "mba-dev-005",
      timeAgo: "2 hours ago",
      type: "admin",
      duration: "24 hours"
    },
    {
      id: "5",
      user: "System",
      action: "completed vulnerability scan",
      device: "All devices",
      deviceId: "sys-scan-all",
      timeAgo: "3 hours ago",
      type: "security",
      duration: "No critical issues"
    }
  ];

  // Function to render the appropriate icon based on activity type
  const renderIcon = (type: string) => {
    switch (type) {
      case "admin":
        return <Shield className="h-5 w-5 text-blue-600" />;
      case "installation":
        return <Download className="h-5 w-5 text-green-600" />;
      case "security":
        return <Lock className="h-5 w-5 text-purple-600" />;
      case "update":
        return <Download className="h-5 w-5 text-amber-600" />;
      case "alert":
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case "mdm":
        return <Terminal className="h-5 w-5 text-gray-600" />;
      default:
        return <Cog className="h-5 w-5 text-gray-600" />;
    }
  };

  // Function to determine details text based on activity type
  const getDetailsText = (activity: Activity) => {
    switch (activity.type) {
      case "admin":
        return `Administrative access: ${activity.duration}`;
      case "installation":
        return `Software deployment: ${activity.duration}`;
      case "security":
        return activity.duration ? `Security policy: ${activity.duration}` : "Security operation";
      case "update":
        return activity.duration ? `Version: ${activity.duration}` : "System update";
      case "alert":
        return "Security alert - requires attention";
      case "mdm":
        return activity.duration ? `MDM action: ${activity.duration}` : "MDM operation";
      default:
        return "";
    }
  };

  return (
    <GlassCard variant="default">
      <GlassCardHeader className="pb-2">
        <GlassCardTitle className="text-lg font-medium">Recent Activities</GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent>
        <ul className="space-y-4">
          {activities.map((activity) => (
            <li key={activity.id} className="flex items-start justify-between py-2">
              <div className="flex items-start space-x-4">
                <div className="mt-0.5">
                  {renderIcon(activity.type)}
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-900">
                    {`${activity.user} ${activity.action} on ${activity.device}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {getDetailsText(activity)}
                  </p>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {activity.timeAgo}
              </div>
            </li>
          ))}
        </ul>
      </GlassCardContent>
    </GlassCard>
  );
}
